import { cableIsNegativePath, isFuseHolderVariant } from "./auto-fuse";
import { getEffectiveCableFuse } from "./cable-current";
import { computeSystemCableSpec } from "./cable-spec";
import { getDefinition, type DefLookup } from "./catalog";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "./settings/auto-size-vdrop-target";
import { findNode } from "./system-lookup";
import type { Cable, System, SystemNode } from "./types";

export type ConfirmConnectionOptions = {
  lengthM: number;
  /** When true, rate the cable's own fuse. Ignored on negative/ground legs. */
  autoFuse: boolean;
  /** When false, the user picked the wire size manually. */
  autoCable: boolean;
  wireSizeMm2Override?: number;
};

export type PendingEndpoints = {
  fromNodeId: string;
  fromTerminalId: string;
  toNodeId: string;
  toTerminalId: string;
};

export type CableConfirmPlan = { kind: "direct"; cable: Omit<Cable, "id"> };

/**
 * Sizes against a *tentative* system that already includes this cable, so the
 * persisted fuse matches what the preview showed and what the diagnostic will
 * check. Returns null for endpoints it can't resolve — treat as a no-op.
 */
export function planCableConfirmation(
  pending: PendingEndpoints,
  opts: ConfirmConnectionOptions,
  system: System,
  defLookup: DefLookup = getDefinition,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): CableConfirmPlan | null {
  const fromNode = findNode(system, pending.fromNodeId);
  const toNode = findNode(system, pending.toNodeId);
  if (
    !fromNode ||
    !toNode ||
    fromNode.kind !== "component" ||
    toNode.kind !== "component"
  ) {
    return null;
  }

  const fromDef = defLookup(fromNode.definitionId, fromNode.customDefinition);
  const toDef = defLookup(toNode.definitionId, toNode.customDefinition);
  if (!fromDef || !toDef) return null;

  const wireSizeMm2Override = opts.autoCable ? null : opts.wireSizeMm2Override ?? null;
  const directCable: Cable = {
    id: "preview",
    fromNodeId: pending.fromNodeId,
    fromTerminalId: pending.fromTerminalId,
    toNodeId: pending.toNodeId,
    toTerminalId: pending.toTerminalId,
    lengthM: opts.lengthM,
    wireSizeMm2Override,
  };
  const tentativeSystem = { ...system, cables: [...system.cables, directCable] };
  const spec = computeSystemCableSpec(
    directCable,
    tentativeSystem,
    defLookup,
    undefined,
    autoSizeTargetPct,
  );
  const fuseRating = spec.fuse;

  const fromTerm = fromDef.terminals.find((t) => t.id === pending.fromTerminalId);
  const toTerm = toDef.terminals.find((t) => t.id === pending.toTerminalId);
  // ABYC: fuses protect the positive conductor, so never auto-fuse a cable
  // touching a negative or ground terminal, whatever the modal forwarded. An
  // AC run is the inlet breaker's to protect (E-11.10.2), never a fuse's.
  const wantsAutoFuse =
    opts.autoFuse && !cableIsNegativePath(fromTerm, toTerm) && !spec.isAc && !spec.sizingVerificationRequired && !spec.fuseRecommendationUnavailableReason;

  // An inline holder is the fuse physically installed, so a cable on its output
  // inherits that rating rather than running auto-fuse.
  const holderRating =
    (isFuseHolderVariant(fromDef) &&
      fromTerm?.direction === "output" &&
      fromNode.fuseAmp) ||
    (isFuseHolderVariant(toDef) &&
      toTerm?.direction === "output" &&
      toNode.fuseAmp) ||
    null;

  // A battery's own `batteryFuse` already reaches this cable through
  // `getEffectiveCableFuse`; mirroring it onto the cable's override double-books
  // the fuse and reports one that isn't installed.
  const tentativeNodesById: Record<string, SystemNode> = Object.fromEntries(
    system.nodes.map((n) => [n.id, n]),
  );
  const inheritedFuseSource = getEffectiveCableFuse(
    directCable,
    tentativeNodesById,
    defLookup,
  ).source.rating;
  const skipMirror = inheritedFuseSource === "battery";

  // `spec.fuse === 0` is "nothing to recommend" (an AC run, or a positive run
  // whose ends resolve no current — bus stud → Anderson connector, switch →
  // battery post with nothing behind it). Never mirror that as a rating: the
  // 1 A the ladder used to answer was written for real and then read back as a
  // 1 A load.
  const fuseAmpOverride =
    holderRating ??
    (wantsAutoFuse && !skipMirror && fuseRating > 0 ? fuseRating : null);

  return {
    kind: "direct",
    cable: {
      fromNodeId: pending.fromNodeId,
      fromTerminalId: pending.fromTerminalId,
      toNodeId: pending.toNodeId,
      toTerminalId: pending.toTerminalId,
      lengthM: opts.lengthM,
      wireSizeMm2Override,
      fuseAmpOverride,
      // Only this cable's computed choice may be refreshed later. Inherited
      // holder/post declarations and explicit no-fuse choices are not auto picks.
      ...(holderRating == null && wantsAutoFuse && !skipMirror && fuseRating > 0
        ? { fuseAmpAuto: true } : {}),
    },
  };
}
